import { ArrowUpTrayIcon, Bars3BottomLeftIcon, BarsArrowDownIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronDownIcon, LightBulbIcon, MicrophoneIcon, PaperAirplaneIcon, QuestionMarkCircleIcon, TableCellsIcon, TrashIcon } from '@heroicons/react/24/outline';
import { ActionIcon, AppShell, Box, Button, Collapse, Container, Divider, FileButton, Group, Modal, NumberInput, Paper, ScrollArea, Stack, Text, Textarea, TextInput, Title, Tooltip, useMantineColorScheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { CVPreviewSidebar } from '../../components/common/CVPreviewSidebar';
import { MermaidChart } from '../../components/common/MermaidChart';
import { ThinkingLoader } from '../../components/common/ThinkingLoader';
import { Header } from '../../components/layout/Header';
import { PREDEFINED_PROMPTS } from '../../data/chatPrompts';
import { useColors } from '../../hooks/useColors';
import { useSpeech } from '../../hooks/useSpeech';
import { useTranslation } from '../../hooks/useTranslation';
import { ChatAboutCandidatesService } from '../../services/chatAboutCandidatesService';
import { ChatType } from '../../types/chat';
import { normalizeChartData } from '../../utils/chartDataNormalizer';

// Individual prompt item component to handle hover state properly
interface PromptItemProps {
  prompt: string;
  onClick: () => void;
}

const PromptItem = React.memo(({ prompt, onClick }: PromptItemProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';

  return (
    <Paper
      p="xs"
      style={{
        backgroundColor: isHovered
          ? (isDark ? 'var(--mantine-color-dark-5)' : 'var(--mantine-color-gray-2)')
          : (isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)'),
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
    >
      <Text size="sm">{prompt}</Text>
    </Paper>
  );
});

interface ChatResponse {
  response: string;
  citations: string;
}

interface Message {
  text: string;
  isUser: boolean;
  citations?: string;
  agentName?: string;
  agentType?: string;
  isStreaming?: boolean;
}

export function ChatAboutCandidatesPage() {
  const [message, setMessage] = useState('');
  const [chatType, setChatType] = useState(ChatType.General);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const { t } = useTranslation();
  const colors = useColors();
  const isMobile = useMediaQuery('(max-width: 1024px)');
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [teamComposerOpen, setTeamComposerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const fileResetRef = useRef<() => void>(null);
  const [selectedPersonId, setSelectedPersonId] = useState<number | null>(null);
  const [streamingLines, setStreamingLines] = useState<string[]>([]);
  const streamingTextRef = useRef<string>('');
  const [showHelperThinking, setShowHelperThinking] = useState<{ [key: number]: boolean }>({});
  const [showMarkdownHelp, setShowMarkdownHelp] = useState(false);
  const [editorMode, setEditorMode] = useState<'inline' | 'rtf'>('inline');
  const [showTableModal, setShowTableModal] = useState(false);
  const [tableRows, setTableRows] = useState<number>(3);
  const [tableColumns, setTableColumns] = useState<number>(3);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const chatServiceRef = useRef(new ChatAboutCandidatesService());

  const processStreamingText = useCallback((text: string) => {
    streamingTextRef.current += text;

    const allLines = streamingTextRef.current.split('\n');
    const completeLines = allLines.length > 1 ? allLines.slice(0, -1) : [];
    const currentLine = allLines[allLines.length - 1];

    let queue: string[] = [];

    if (completeLines.length > 0) {
      queue = completeLines.slice(-5);
    }

    if (currentLine || queue.length === 0) {
      queue.push(currentLine);
    }

    if (queue.length > 5) {
      queue = queue.slice(-5);
    }

    setStreamingLines(queue);
  }, []);

  const handleSendWithText = async (textToSend: string) => {
    if (textToSend.trim()) {
      setIsLoading(true);
      setError(null);
      setStreamingLines([]);
      streamingTextRef.current = '';

      const userMessage: Message = { text: textToSend, isUser: true };
      setMessages(prev => [...prev, userMessage]);

      const currentTurnStartIndex = messages.length + 1;
      let accumulatedCitations = '';
      const agentResponses = new Map<string, string>();
      const agentOrder: string[] = [];

      setMessages(prev => [...prev, { text: '', isUser: false, isStreaming: true }]);

      try {
        const chatService = chatServiceRef.current;

        for await (const chunk of chatService.streamChatMessage(textToSend)) {
          if (chunk.response && chunk.agentName && chunk.agentType) {
            const agentKey = `${chunk.agentType}-${chunk.agentName}`;

            if (!agentResponses.has(agentKey)) {
              agentOrder.push(agentKey);
            }

            const currentText = agentResponses.get(agentKey) || '';
            const newText = currentText + chunk.response;
            agentResponses.set(agentKey, newText);

            if (agentKey.startsWith('HELPER-')) {
              processStreamingText(chunk.response);
            }

            setMessages(prev => {
              const previousMessages = prev.slice(0, currentTurnStartIndex);
              const currentTurnMessages: Message[] = [];

              agentOrder.forEach(key => {
                const text = agentResponses.get(key) || '';
                const [agentType, ...nameParts] = key.split('-');
                const agentName = nameParts.join('-');
                currentTurnMessages.push({
                  text,
                  isUser: false,
                  agentName,
                  agentType,
                  citations: accumulatedCitations,
                  isStreaming: true
                });
              });

              return [...previousMessages, ...currentTurnMessages];
            });
          }

          if (chunk.citations) {
            accumulatedCitations = chunk.citations;

            setMessages(prev => {
              if (prev.length === 0) return prev;
              const newMessages = [...prev];
              const lastMsg = newMessages[newMessages.length - 1];
              if (!lastMsg.isUser) {
                newMessages[newMessages.length - 1] = {
                  ...lastMsg,
                  citations: accumulatedCitations
                };
              }
              return newMessages;
            });
          }
        }

        setMessages(prev => {
          const previousMessages = prev.slice(0, currentTurnStartIndex);
          const currentTurnMessages: Message[] = [];

          agentOrder.forEach(key => {
            const text = agentResponses.get(key) || '';
            const [agentType, ...nameParts] = key.split('-');
            const agentName = nameParts.join('-');
            currentTurnMessages.push({
              text,
              isUser: false,
              agentName,
              agentType,
              citations: accumulatedCitations,
              isStreaming: false
            });
          });

          return [...previousMessages, ...currentTurnMessages];
        });

        setStreamingLines([]);
        streamingTextRef.current = '';

        const outputResponse = Array.from(agentResponses.entries())
          .find(([key]) => key.startsWith('OUTPUT-'))?.[1] || '';
        if (outputResponse) {
          speakTextStreaming(outputResponse);
        }

        setMessage('');
      } catch (err) {
        console.error('Error sending message:', err);
        setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');

        setStreamingLines([]);
        streamingTextRef.current = '';

        setMessages(prev => [...prev, {
          text: 'Sorry, I encountered an error while processing your message. Please try again.',
          isUser: false,
          isStreaming: false
        }]);

        speakTextStreaming('Sorry, I encountered an error while processing your message. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSend = async () => {
    await handleSendWithText(message);
  };

  const {
    isRecording,
    ttsEnabled,
    speakTextStreaming,
    handleMicrophoneClick,
    toggleTTS,
    stopSpeech,
    isSpeechAvailable
  } = useSpeech({
    autoSendOnRecognition: true,
    onAutoSend: handleSendWithText,
    onSetMessage: setMessage
  });

  useEffect(() => {
    return () => {
      stopSpeech();
    };
  }, [stopSpeech]);

  const handleClear = () => {
    setMessages([]);
    setError(null);
    setShowHelperThinking({});
    chatServiceRef.current.clearChat();
  };

  // Insert markdown syntax at cursor position
  const insertMarkdown = useCallback((before: string, after: string = '', placeholder: string = '') => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = message.substring(start, end);
    const textToInsert = selectedText || placeholder;

    const newText = message.substring(0, start) + before + textToInsert + after + message.substring(end);
    setMessage(newText);

    // Set cursor position after insertion
    setTimeout(() => {
      const newCursorPos = start + before.length + textToInsert.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [message]);

  // Insert markdown block (like lists or tables)
  const insertBlock = useCallback((block: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;

    // Add newlines before and after if needed
    const beforeText = message.substring(0, start);
    const afterText = message.substring(end);
    const needsNewlineBefore = beforeText.length > 0 && !beforeText.endsWith('\n');
    const needsNewlineAfter = afterText.length > 0 && !afterText.startsWith('\n');

    const prefix = needsNewlineBefore ? '\n' : '';
    const suffix = needsNewlineAfter ? '\n' : '';

    const newText = beforeText + prefix + block + suffix + afterText;
    setMessage(newText);

    // Set cursor position after the block
    setTimeout(() => {
      const newCursorPos = start + prefix.length + block.length;
      textarea.focus();
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  }, [message]);

  // Generate table markdown with specified rows and columns
  const generateTable = useCallback((rows: number, columns: number) => {
    // Generate header row
    const headers = Array.from({ length: columns }, (_, i) => `Header ${i + 1}`).join(' | ');
    const headerRow = `| ${headers} |`;

    // Generate separator row
    const separators = Array.from({ length: columns }, () => '----------').join('|');
    const separatorRow = `|${separators}|`;

    // Generate data rows
    const dataRows = Array.from({ length: rows }, (_, rowIndex) => {
      const cells = Array.from({ length: columns }, (_, colIndex) =>
        `Cell ${rowIndex * columns + colIndex + 1}`
      ).join(' | ');
      return `| ${cells} |`;
    }).join('\n');

    return `${headerRow}\n${separatorRow}\n${dataRows}`;
  }, []);

  // Markdown toolbar actions
  const handleBold = useCallback(() => insertMarkdown('**', '**', 'bold text'), [insertMarkdown]);
  const handleItalic = useCallback(() => insertMarkdown('*', '*', 'italic text'), [insertMarkdown]);
  const handleCode = useCallback(() => insertMarkdown('`', '`', 'code'), [insertMarkdown]);
  const handleHeading = useCallback(() => insertMarkdown('### ', '', 'Heading'), [insertMarkdown]);
  const handleLink = useCallback(() => insertMarkdown('[', '](url)', 'link text'), [insertMarkdown]);
  const handleBulletList = useCallback(() => insertBlock('- Item 1\n- Item 2\n- Item 3'), [insertBlock]);
  const handleNumberedList = useCallback(() => insertBlock('1. Item 1\n2. Item 2\n3. Item 3'), [insertBlock]);
  const handleTable = useCallback(() => setShowTableModal(true), []);
  const handleTableInsert = useCallback(() => {
    const tableMarkdown = generateTable(tableRows, tableColumns);
    insertBlock(tableMarkdown);
    setShowTableModal(false);
  }, [generateTable, tableRows, tableColumns, insertBlock]);
  const handleCodeBlock = useCallback(() => insertBlock('```\ncode here\n```'), [insertBlock]);
  const handleQuote = useCallback(() => insertMarkdown('> ', '', 'quote text'), [insertMarkdown]);

  const handleKeyPress = (event: React.KeyboardEvent) => {
    // Ctrl/Cmd + Enter to send (allow Shift+Enter and regular Enter for newlines in textarea)
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      handleSend();
      return;
    }

    // Keyboard shortcuts for formatting
    if (event.ctrlKey || event.metaKey) {
      switch (event.key.toLowerCase()) {
        case 'b':
          event.preventDefault();
          handleBold();
          break;
        case 'i':
          event.preventDefault();
          handleItalic();
          break;
      }
    }
  };

  const formatCitations = useCallback((citations: string) => {
    return citations.split('\n').filter(line => line.trim());
  }, []);

  const markdownComponents = useMemo(() => ({
    p: ({ children }: any) => <>{children}</>,
    table: ({ children }: any) => (
      <div className="markdown-table-wrapper">
        <table className="markdown-table">
          {children}
        </table>
      </div>
    ),
    code({ node, className, children, ...props }: any) {
      const match = /language-(\w+)/.exec(className || '');
      if (match && match[1] === 'mermaid') {
        return <MermaidChart chart={String(children).trim()} />;
      }
      return (
        <code className={className} {...props}>
          {children}
        </code>
      );
    }
  }), []);

  const renderMessage = useCallback((msg: Message, index: number) => {
    const isHelperAgent = msg.agentType === 'HELPER';

    const hasOutputAfter = isHelperAgent && messages.slice(index + 1).some(m =>
      m.agentType === 'OUTPUT' && (m.text || m.isStreaming)
    );

    if (!msg.isUser && msg.isStreaming && isHelperAgent && streamingLines.length > 0 && !hasOutputAfter) {
      return (
        <Box
          key={index}
          style={{
            marginBottom: 12,
            alignSelf: 'flex-start',
            maxWidth: '85%',
          }}
        >
          <Stack gap={2} style={{ padding: '4px 0' }}>
            {streamingLines.map((line, lineIndex) => (
              <Text
                key={lineIndex}
                size="sm"
                style={{
                  color: isDark ? 'var(--mantine-color-gray-5)' : 'var(--mantine-color-gray-5)',
                  fontFamily: 'monospace',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  opacity: 0.6,
                }}
              >
                {line || '\u00A0'}
              </Text>
            ))}
          </Stack>
        </Box>
      );
    }

    if (!msg.isUser && isHelperAgent && hasOutputAfter && !showHelperThinking[index]) {
      return null;
    }

    if (!msg.isUser && isHelperAgent && hasOutputAfter && showHelperThinking[index]) {
      return (
        <Box
          key={index}
          style={{
            marginBottom: 4,
            alignSelf: 'flex-start',
            maxWidth: '85%',
          }}
        >
          <Paper
            p="xs"
            style={{
              backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-2)',
              borderRadius: '8px',
              border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-4)'}`,
              opacity: 0.8,
            }}
          >
            {msg.agentName && (
              <Text size="xs" fw={600} mb={4} c="dimmed" style={{ fontFamily: 'monospace' }}>
                {msg.agentName}
              </Text>
            )}
            <Box
              style={{
                wordBreak: 'break-word',
                fontSize: 'var(--mantine-font-size-xs)',
                lineHeight: 1.4,
                color: isDark ? 'var(--mantine-color-dark-2)' : 'var(--mantine-color-gray-6)',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
              }}
            >
              {msg.text}
            </Box>
          </Paper>
        </Box>
      );
    }

    if (!msg.isUser && !msg.text) {
      return (
        <Box
          key={index}
          style={{
            marginBottom: 12,
            alignSelf: 'flex-start',
            maxWidth: '85%',
          }}
        >
          <Paper
            p="xs"
            style={{
              backgroundColor: colors.getChatColors().botMessage,
              borderRadius: '12px',
              borderBottomLeftRadius: '4px',
            }}
          >
            <ThinkingLoader />
          </Paper>
        </Box>
      );
    }

    let processedText = msg.text;
    if (!msg.isUser && (msg.text.includes('xychart-beta') || msg.text.includes('xychart'))) {
      const mermaidCodeBlockMatch = msg.text.match(/```mermaid\n([\s\S]*?)\n?```/);
      if (mermaidCodeBlockMatch) {
        const rawChartContent = mermaidCodeBlockMatch[1];
        const chartContent = rawChartContent.replace(/\n?\s*```\s*$/, '').trim();
        const normalizedChartContent = normalizeChartData(chartContent);

        processedText = msg.text.replace(
          mermaidCodeBlockMatch[0],
          `\n\`\`\`mermaid\n${normalizedChartContent}\n\`\`\`\n`
        );
      } else {
        const codeBlockMatch = msg.text.match(/```(xychart-beta|xychart)\n([\s\S]*?)\n```/);
        if (codeBlockMatch) {
          const chartContent = codeBlockMatch[2].trim();
          const fullChartContent = `${codeBlockMatch[1]}\n${chartContent}`;
          const normalizedChartContent = normalizeChartData(fullChartContent);

          processedText = msg.text.replace(
            codeBlockMatch[0],
            `\n\`\`\`mermaid\n${normalizedChartContent}\n\`\`\`\n`
          );
        } else {
          const chartMatch = msg.text.match(/(xychart-beta|xychart)[\s\S]*?(?=\n\n|$)/);
          if (chartMatch) {
            const chartContent = chartMatch[0].trim();
            const normalizedChartContent = normalizeChartData(chartContent);

            processedText = msg.text.replace(
              chartContent,
              `\n\`\`\`mermaid\n${normalizedChartContent}\n\`\`\`\n`
            );
          }
        }
      }
    }

    const isOutputAgent = msg.agentType === 'OUTPUT';
    const helperIndices: number[] = [];
    if (isOutputAgent) {
      const prevOutputIndex = messages.slice(0, index).map((m, i) => ({ m, i })).filter(x => x.m.agentType === 'OUTPUT').pop()?.i ?? -1;
      messages.forEach((m, idx) => {
        if (idx < index && idx > prevOutputIndex && m.agentType === 'HELPER') {
          helperIndices.push(idx);
        }
      });
    }
    const hasHelperBefore = helperIndices.length > 0;

    return (
      <Box
        key={index}
        style={{
          marginBottom: 12,
          alignSelf: msg.isUser ? 'flex-end' : 'flex-start',
          maxWidth: '85%',
        }}
      >
        {isOutputAgent && hasHelperBefore && (
          <Group gap="xs" mb={4}>
            <ActionIcon
              size="xs"
              variant={showHelperThinking[helperIndices[0]] ? "filled" : "subtle"}
              color="gray"
              onClick={() => setShowHelperThinking(prev => {
                const next = { ...prev };
                const newValue = !prev[helperIndices[0]];
                helperIndices.forEach(idx => { next[idx] = newValue; });
                return next;
              })}
              title={showHelperThinking[helperIndices[0]] ? "Hide thinking" : "Show thinking"}
            >
              <LightBulbIcon width={14} />
            </ActionIcon>
            <Text size="xs" c="dimmed" style={{ fontStyle: 'italic' }}>
              {showHelperThinking[helperIndices[0]] ? "Hide thinking" : "Show thinking"}
            </Text>
          </Group>
        )}
        <Paper
          p="xs"
          style={{
            backgroundColor: msg.isUser
              ? colors.getChatColors().userMessage
              : colors.getChatColors().botMessage,
            borderRadius: '12px',
            borderBottomRightRadius: msg.isUser ? '4px' : '12px',
            borderBottomLeftRadius: msg.isUser ? '12px' : '4px',
            color: msg.isUser && isDark ? 'white' : undefined,
          }}
        >
          <Box
            style={{
              wordBreak: 'break-word',
              fontSize: 'var(--mantine-font-size-sm)',
              lineHeight: 1.5,
              '& p': { margin: 0 },
              '& ul, & ol': { margin: 0, paddingLeft: '0.75em' },
              '& li': { margin: 0 },
              '& code': {
                backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)',
                padding: '0.05em 0.2em',
                borderRadius: '3px',
                fontSize: '0.9em',
              },
              '& pre': {
                backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)',
                padding: '0.2em',
                borderRadius: '4px',
                overflowX: 'auto',
              },
              '& blockquote': {
                borderLeft: `3px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-4)'}`,
                margin: 0,
                paddingLeft: '0.5em',
                color: isDark ? 'var(--mantine-color-dark-2)' : 'var(--mantine-color-gray-7)',
              },
              '& a': {
                color: isDark ? 'var(--mantine-color-blue-4)' : 'var(--mantine-color-blue-7)',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' },
              },

            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {processedText}
            </ReactMarkdown>
          </Box>
        </Paper>
        {msg.citations && (
          <Stack gap={4} mt={5}>
            {formatCitations(msg.citations).map((citation, citationIndex) => (
              <Text
                key={citationIndex}
                size="xs"
                c="blue"
                style={{
                  fontSize: '11px',
                  cursor: 'pointer',
                  textDecoration: 'none',
                }}
                onClick={() => {
                  const id = Number(citation.split(':')[0].trim());
                  if (!isNaN(id)) setSelectedPersonId(id);
                }}
                onMouseOver={e => (e.currentTarget.style.textDecoration = 'underline')}
                onMouseOut={e => (e.currentTarget.style.textDecoration = 'none')}
              >
                {`${citation.split(':')[1].trim()}: ${citation.split(':')[0].trim()}`}
              </Text>
            ))}
          </Stack>
        )}
      </Box>
    );
  }, [colors, isDark, formatCitations, markdownComponents, streamingLines, messages, showHelperThinking]);

  const handleFileUpload = useCallback(async (file: File | null) => {
    if (!file) return;
    setIsLoading(true);
    setError(null);
    setStreamingLines([]);
    streamingTextRef.current = '';

    const chatService = chatServiceRef.current;
    const userMessage: Message = { text: `Uploaded file: ${file.name}`, isUser: true };
    setMessages(prev => [...prev, userMessage]);

    const currentTurnStartIndex = messages.length + 1;
    let accumulatedCitations = '';
    const agentResponses = new Map<string, string>();
    const agentOrder: string[] = [];

    setMessages(prev => [...prev, { text: '', isUser: false, isStreaming: true }]);

    try {
      for await (const chunk of chatService.streamGenerateOrgChartFromFile(file, chatService.getCurrentChatId() || undefined)) {
        if (chunk.chatId && chatService.getCurrentChatId() !== chunk.chatId) {
          chatService.setChatId(chunk.chatId);
        }

        if (chunk.response && chunk.agentName && chunk.agentType) {
          const agentKey = `${chunk.agentType}-${chunk.agentName}`;

          if (!agentResponses.has(agentKey)) {
            agentOrder.push(agentKey);
          }

          const currentText = agentResponses.get(agentKey) || '';
          const newText = currentText + chunk.response;
          agentResponses.set(agentKey, newText);

          if (agentKey.startsWith('HELPER-')) {
            processStreamingText(chunk.response);
          }

          setMessages(prev => {
            const previousMessages = prev.slice(0, currentTurnStartIndex);
            const currentTurnMessages: Message[] = [];

            agentOrder.forEach(key => {
              const text = agentResponses.get(key) || '';
              const [agentType, ...nameParts] = key.split('-');
              const agentName = nameParts.join('-');
              currentTurnMessages.push({
                text,
                isUser: false,
                agentName,
                agentType,
                citations: accumulatedCitations,
                isStreaming: true
              });
            });

            return [...previousMessages, ...currentTurnMessages];
          });
        }

        if (chunk.citations) {
          accumulatedCitations = chunk.citations;

          setMessages(prev => {
            if (prev.length === 0) return prev;
            const newMessages = [...prev];
            const lastMsg = newMessages[newMessages.length - 1];
            if (!lastMsg.isUser) {
              newMessages[newMessages.length - 1] = {
                ...lastMsg,
                citations: accumulatedCitations
              };
            }
            return newMessages;
          });
        }
      }

      setMessages(prev => {
        const previousMessages = prev.slice(0, currentTurnStartIndex);
        const currentTurnMessages: Message[] = [];

        agentOrder.forEach(key => {
          const text = agentResponses.get(key) || '';
          const [agentType, ...nameParts] = key.split('-');
          const agentName = nameParts.join('-');
          currentTurnMessages.push({
            text,
            isUser: false,
            agentName,
            agentType,
            citations: accumulatedCitations,
            isStreaming: false
          });
        });

        return [...previousMessages, ...currentTurnMessages];
      });

      setStreamingLines([]);
      streamingTextRef.current = '';

      const outputResponse = Array.from(agentResponses.entries())
        .find(([key]) => key.startsWith('OUTPUT-'))?.[1] || '';
      if (outputResponse) {
        speakTextStreaming(outputResponse);
      }
    } catch (err) {
      console.error('Error processing file:', err);
      setError(err instanceof Error ? err.message : 'Failed to process file. Please try again.');

      setStreamingLines([]);
      streamingTextRef.current = '';

      setMessages(prev => [...prev, {
        text: 'Sorry, I encountered an error while processing your file. Please try again.',
        isUser: false,
        isStreaming: false
      }]);

      speakTextStreaming('Sorry, I encountered an error while processing your file. Please try again.');
    } finally {
      setIsLoading(false);
      if (fileResetRef.current) fileResetRef.current();
    }
  }, [chatServiceRef, messages.length, processStreamingText, speakTextStreaming]);

  return (
    <AppShell
      header={{ height: 60 }}
      padding="md"
    >
      <AppShell.Header>
        <Header />
      </AppShell.Header>
      <AppShell.Main>
        <Container size="xxl" py="md">
          <Box
            style={{
              display: 'flex',
              flexDirection: (isMobile || (!isMobile && sidebarCollapsed)) ? 'column' : 'row',
              gap: isMobile ? '12px' : '32px',
              alignItems: 'stretch',
              marginRight: !isMobile && selectedPersonId ? 400 : 0,
            }}
          >
            {/* Prompts Sidebar */}
            {(isMobile || (!isMobile && sidebarCollapsed)) ? (
              <Stack gap={0}>
                {/* Suggested Prompts accordion */}
                <Box>
                  <Button
                    variant="subtle"
                    fullWidth
                    justify="space-between"
                    rightSection={
                      <ChevronDownIcon
                        width={16}
                        style={{
                          transform: promptsOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.25s ease',
                        }}
                      />
                    }
                    onClick={() => setPromptsOpen(prev => !prev)}
                    style={{
                      backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                      border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                      borderRadius: promptsOpen ? '12px 12px 0 0' : '12px',
                      color: isDark ? 'var(--mantine-color-white)' : 'var(--mantine-color-dark-9)',
                      fontWeight: 600,
                      fontSize: 'var(--mantine-font-size-md)',
                      height: 44,
                    }}
                  >
                    Suggested Prompts
                  </Button>
                  <Collapse in={promptsOpen} style={{ overflow: 'visible' }}>
                    <Paper
                      p="md"
                      style={{
                        backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                        border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                        borderTop: 'none',
                        borderRadius: '0 0 12px 12px',
                      }}
                    >
                      <ScrollArea h="calc(100svh - 340px)" type="scroll" offsetScrollbars>
                        <Stack gap="md">
                          {PREDEFINED_PROMPTS.filter(c => c.category !== 'Team Composer').map((category, index) => (
                            <Box key={index}>
                              <Text fw={500} mb="xs" c={isDark ? 'blue.4' : 'blue.7'}>
                                {category.category}
                              </Text>
                              <Stack gap="xs">
                                {category.prompts.map((prompt, promptIndex) => (
                                  <PromptItem
                                    key={promptIndex}
                                    prompt={prompt}
                                    onClick={() => {
                                      setMessage(prompt);
                                      setChatType(category.chatType);
                                      setPromptsOpen(false);
                                    }}
                                  />
                                ))}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </ScrollArea>
                    </Paper>
                  </Collapse>
                </Box>

                {/* Team Composer accordion */}
                <Box mt="xs">
                  <Button
                    variant="subtle"
                    fullWidth
                    justify="space-between"
                    rightSection={
                      <ChevronDownIcon
                        width={16}
                        style={{
                          transform: teamComposerOpen ? 'rotate(180deg)' : 'none',
                          transition: 'transform 0.25s ease',
                        }}
                      />
                    }
                    onClick={() => setTeamComposerOpen(prev => !prev)}
                    style={{
                      backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                      border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                      borderRadius: teamComposerOpen ? '12px 12px 0 0' : '12px',
                      color: isDark ? 'var(--mantine-color-white)' : 'var(--mantine-color-dark-9)',
                      fontWeight: 600,
                      fontSize: 'var(--mantine-font-size-md)',
                      height: 44,
                    }}
                  >
                    Team Composer
                  </Button>
                  <Collapse in={teamComposerOpen}>
                    <Paper
                      p="md"
                      style={{
                        backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                        border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                        borderTop: 'none',
                        borderRadius: '0 0 12px 12px',
                      }}
                    >
                      <Text size="xs" c="dimmed" mb="md" style={{ lineHeight: 1.4 }}>
                        Upload a file with project description, AI will help you to compose a team structure and select the best candidates
                      </Text>
                      <FileButton onChange={handleFileUpload} accept=".doc,.docx" resetRef={fileResetRef}>
                        {(props) => (
                          <Button
                            {...props}
                            variant="light"
                            size="sm"
                            fullWidth
                            disabled={isLoading}
                            title="Upload file for org chart"
                          >
                            <ArrowUpTrayIcon width={14} style={{ marginRight: '4px' }} />
                            Upload Project File
                          </Button>
                        )}
                      </FileButton>
                    </Paper>
                  </Collapse>
                </Box>
              </Stack>
            ) : (
              <Stack
                gap="md"
                style={{
                  width: 450,
                  minWidth: 300,
                  flexShrink: 0,
                }}
              >
                {/* Suggested Prompts panel */}
                <Paper
                  shadow="sm"
                  p="md"
                  style={{
                    flex: 1,
                    minHeight: 0,
                    backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                    border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                    borderRadius: '12px',
                    overflow: 'hidden',
                  }}
                >
                  <Title order={3} mb="md" c={isDark ? 'white' : 'dark'}>Suggested Prompts</Title>
                  <ScrollArea h="calc(100vh - 440px)">
                    <Stack gap="md">
                      {PREDEFINED_PROMPTS.filter(c => c.category !== 'Team Composer').map((category, index) => (
                        <Box key={index}>
                          <Text fw={500} mb="xs" c={isDark ? 'blue.4' : 'blue.7'}>
                            {category.category}
                          </Text>
                          <Stack gap="xs">
                            {category.prompts.map((prompt, promptIndex) => (
                              <PromptItem
                                key={promptIndex}
                                prompt={prompt}
                                onClick={() => {
                                  setMessage(prompt);
                                  setChatType(category.chatType);
                                }}
                              />
                            ))}
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  </ScrollArea>
                </Paper>

                {/* Team Composer panel */}
                <Paper
                  shadow="sm"
                  p="md"
                  style={{
                    backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                    border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                    borderRadius: '12px',
                  }}
                >
                  <Title order={3} mb="xs" c={isDark ? 'white' : 'dark'}>Team Composer</Title>
                  <Text size="xs" c="dimmed" mb="md" style={{ lineHeight: 1.4 }}>
                    Upload a file with project description, AI will help you to compose a team structure and select the best candidates
                  </Text>
                  <FileButton onChange={handleFileUpload} accept=".doc,.docx" resetRef={fileResetRef}>
                    {(props) => (
                      <Button
                        {...props}
                        variant="light"
                        size="sm"
                        fullWidth
                        disabled={isLoading}
                        title="Upload file for org chart"
                      >
                        <ArrowUpTrayIcon width={14} style={{ marginRight: '4px' }} />
                        Upload Project File
                      </Button>
                    )}
                  </FileButton>
                </Paper>
              </Stack>
            )}

            {/* Chat Window */}
            <Paper
              shadow="xl"
              p="md"
              style={{
                flex: 2,
                minWidth: 0,
                height: isMobile ? 'calc(100svh - 170px)' : undefined,
                maxHeight: isMobile ? undefined : 'calc(100vh - 200px)',
                backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                border: `2px solid ${colors.getSemanticColorValue('borderLight')}`,
                borderRadius: '12px',
                position: 'relative',
                boxShadow: isDark
                  ? '0 8px 32px rgba(0, 0, 0, 0.35), 0 3px 8px rgba(0, 0, 0, 0.2)'
                  : '0 8px 32px rgba(0, 0, 0, 0.15), 0 3px 8px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
              }}
            >
              {/* Chat panel header with sidebar toggle (desktop only) */}
              {!isMobile && (
                <Group justify="flex-start" mb={4} style={{ flexShrink: 0 }}>
                  <Tooltip label={sidebarCollapsed ? 'Restore sidebar' : 'Expand chat — collapse sidebar'} position="right">
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setSidebarCollapsed(prev => !prev)}
                    >
                      {sidebarCollapsed
                        ? <ChevronDoubleRightIcon width={15} />
                        : <ChevronDoubleLeftIcon width={15} />}
                    </ActionIcon>
                  </Tooltip>
                </Group>
              )}
              <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
                {messages.length === 0 && (
                  <Box
                    style={{
                      padding: '12px',
                      marginBottom: '4px',
                      flexShrink: 0,
                    }}
                  >
                    <Text size="sm" fw={500} c={isDark ? 'blue.4' : 'dimmed'} mb={4}>
                      Chat About Candidates
                    </Text>
                    <Text size="xs" c="dimmed" style={{ lineHeight: 1.4 }}>
                      Welcome! Ask questions about candidates and their CVs.
                    </Text>
                  </Box>
                )}
                {messages.length > 0 ? (
                  <Stack gap="xs">
                    {messages.map((msg, index) => renderMessage(msg, index))}
                  </Stack>
                ) : (
                  <Box style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Text size="sm" c="dimmed" ta="center">No messages yet. Start a conversation!</Text>
                  </Box>
                )}
                {error && (
                  <Text color="red" size="sm" ta="center" style={{ flexShrink: 0 }}>{error}</Text>
                )}
                {isLoading && (
                  <Text size="sm" c="dimmed" ta="center" style={{ flexShrink: 0 }}>AI is typing...</Text>
                )}
              </Box>
              {editorMode === 'inline' ? (
                /* Inline Mode - Simple Text Input */
                <Group mt="md" style={{ flexShrink: 0, marginTop: 'auto' }}>
                  <TextInput
                    placeholder="Type a message..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    style={{ flex: 1 }}
                    disabled={isLoading}
                  />
                  <Tooltip label="Rich Text Editor">
                    <ActionIcon
                      variant="subtle"
                      color="blue"
                      size="lg"
                      onClick={() => setEditorMode('rtf')}
                      disabled={isLoading}
                      title="Switch to rich text editor"
                    >
                      <Bars3BottomLeftIcon width={16} />
                    </ActionIcon>
                  </Tooltip>
                  <ActionIcon
                    onClick={handleMicrophoneClick}
                    disabled={isLoading || !isSpeechAvailable()}
                    variant={isRecording ? "filled" : "subtle"}
                    color={isRecording ? "red" : "blue"}
                    size="lg"
                    title={
                      !isSpeechAvailable()
                        ? "Voice recognition not available"
                        : isRecording
                          ? "Stop recording"
                          : "Start voice recording"
                    }
                  >
                    <MicrophoneIcon width={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant={ttsEnabled ? "filled" : "subtle"}
                    color={ttsEnabled ? "green" : "gray"}
                    onClick={toggleTTS}
                    disabled={!isSpeechAvailable()}
                    title={
                      !isSpeechAvailable()
                        ? "Text to speech not available"
                        : ttsEnabled
                          ? "Disable text to speech"
                          : "Enable text to speech"
                    }
                    size="lg"
                  >
                    🔊
                  </ActionIcon>
                  <Button
                    onClick={handleSend}
                    disabled={!message.trim() || isLoading}
                    variant="filled"
                    size="sm"
                    loading={isLoading}
                  >
                    <PaperAirplaneIcon width={16} />
                  </Button>
                  <Button
                    onClick={handleClear}
                    variant="outline"
                    size="sm"
                    color="red"
                    disabled={messages.length === 0}
                    ml={4}
                    title="Clear chat"
                  >
                    <TrashIcon width={16} />
                  </Button>
                </Group>
              ) : (
                /* RTF Mode - Rich Text Editor with Side-by-Side Preview */
                <Box mt="md" style={{ flexShrink: 0, marginTop: 'auto' }}>
                  <Paper
                    p="xs"
                    style={{
                      backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-0)',
                      border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                      borderRadius: '8px',
                    }}
                  >
                    {/* Header with controls */}
                    <Group gap="xs" mb="xs" justify="space-between">
                      <Group gap="xs">
                        <Tooltip label="Switch to inline mode">
                          <Button
                            variant="subtle"
                            size="xs"
                            onClick={() => setEditorMode('inline')}
                            leftSection={<span style={{ fontSize: '12px' }}>←</span>}
                          >
                            Inline Mode
                          </Button>
                        </Tooltip>
                        <Text size="xs" c="dimmed">
                          Ctrl+Enter to send
                        </Text>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={() => setShowMarkdownHelp(!showMarkdownHelp)}
                          title="Markdown help"
                        >
                          <QuestionMarkCircleIcon width={14} />
                        </ActionIcon>
                      </Group>
                      <Group gap="xs">
                        <ActionIcon
                          onClick={handleMicrophoneClick}
                          disabled={isLoading || !isSpeechAvailable()}
                          variant={isRecording ? "filled" : "subtle"}
                          color={isRecording ? "red" : "blue"}
                          size="sm"
                          title={
                            !isSpeechAvailable()
                              ? "Voice recognition not available"
                              : isRecording
                                ? "Stop recording"
                                : "Start voice recording"
                          }
                        >
                          <MicrophoneIcon width={14} />
                        </ActionIcon>
                        <ActionIcon
                          variant={ttsEnabled ? "filled" : "subtle"}
                          color={ttsEnabled ? "green" : "gray"}
                          onClick={toggleTTS}
                          disabled={!isSpeechAvailable()}
                          title={
                            !isSpeechAvailable()
                              ? "Text to speech not available"
                              : ttsEnabled
                                ? "Disable text to speech"
                                : "Enable text to speech"
                          }
                          size="sm"
                        >
                          🔊
                        </ActionIcon>
                      </Group>
                    </Group>

                    <Collapse in={showMarkdownHelp}>
                      <Paper
                        p="xs"
                        mb="xs"
                        style={{
                          backgroundColor: isDark ? 'var(--mantine-color-dark-8)' : 'var(--mantine-color-gray-1)',
                          fontSize: '11px',
                        }}
                      >
                        <Text size="xs" fw={500} mb={4}>Markdown Quick Reference:</Text>
                        <Stack gap={2}>
                          <Text size="xs" c="dimmed">**bold** or __bold__ → <strong>bold</strong></Text>
                          <Text size="xs" c="dimmed">*italic* or _italic_ → <em>italic</em></Text>
                          <Text size="xs" c="dimmed">`code` → <code>inline code</code></Text>
                          <Text size="xs" c="dimmed">- List item → • Bullet list</Text>
                          <Text size="xs" c="dimmed">1. Numbered item → 1. Numbered list</Text>
                          <Text size="xs" c="dimmed">[link](url) → Hyperlink</Text>
                          <Text size="xs" c="dimmed">### Heading → Heading (use #, ##, ###)</Text>
                          <Text size="xs" c="dimmed">&gt; Quote → Block quote</Text>
                          <Text size="xs" c="dimmed">```code``` → Code block (multiline)</Text>
                        </Stack>
                      </Paper>
                    </Collapse>

                    {/* Markdown Toolbar */}
                    <Group gap={4} mb="xs" wrap="wrap">
                      <Tooltip label="Bold (Ctrl+B)">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleBold}
                          disabled={isLoading}
                        >
                          <strong style={{ fontSize: '14px' }}>B</strong>
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Italic (Ctrl+I)">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleItalic}
                          disabled={isLoading}
                        >
                          <em style={{ fontSize: '14px' }}>I</em>
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Inline Code">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleCode}
                          disabled={isLoading}
                        >
                          <code style={{ fontSize: '12px' }}>&lt;/&gt;</code>
                        </ActionIcon>
                      </Tooltip>
                      <Divider orientation="vertical" />
                      <Tooltip label="Heading">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleHeading}
                          disabled={isLoading}
                        >
                          <strong style={{ fontSize: '14px' }}>H</strong>
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Bullet List">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleBulletList}
                          disabled={isLoading}
                        >
                          <Bars3BottomLeftIcon width={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Numbered List">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleNumberedList}
                          disabled={isLoading}
                        >
                          <BarsArrowDownIcon width={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Table">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleTable}
                          disabled={isLoading}
                        >
                          <TableCellsIcon width={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Divider orientation="vertical" />
                      <Tooltip label="Link">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleLink}
                          disabled={isLoading}
                        >
                          <span style={{ fontSize: '14px' }}>🔗</span>
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Quote">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleQuote}
                          disabled={isLoading}
                        >
                          <span style={{ fontSize: '14px' }}>"</span>
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Code Block">
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          size="sm"
                          onClick={handleCodeBlock}
                          disabled={isLoading}
                        >
                          <span style={{ fontSize: '12px' }}>{ }</span>
                        </ActionIcon>
                      </Tooltip>
                    </Group>

                    {/* Side-by-side editor and preview */}
                    <Group align="flex-start" gap="xs" style={{ alignItems: 'stretch' }}>
                      {/* Editor */}
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="xs" c="dimmed" mb={4}>Editor</Text>
                        <Textarea
                          ref={textareaRef}
                          placeholder="Type your message... Use toolbar buttons to add formatting"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onKeyDown={handleKeyPress}
                          disabled={isLoading}
                          minRows={4}
                          maxRows={10}
                          autosize
                          styles={{
                            input: {
                              backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                              border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                              fontSize: 'var(--mantine-font-size-sm)',
                              fontFamily: 'monospace',
                            }
                          }}
                        />
                      </Box>

                      {/* Live Preview */}
                      <Box style={{ flex: 1, minWidth: 0 }}>
                        <Text size="xs" c="dimmed" mb={4}>Live Preview</Text>
                        <Box
                          style={{
                            minHeight: 100,
                            maxHeight: 250,
                            overflowY: 'auto',
                            padding: '8px',
                            backgroundColor: isDark ? 'var(--mantine-color-dark-6)' : 'white',
                            border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                            borderRadius: '4px',
                            fontSize: 'var(--mantine-font-size-sm)',
                            lineHeight: 1.5,
                          }}
                          className="markdown-preview"
                        >
                          {message.trim() ? (
                            <Box
                              style={{
                                '& p': { margin: '0.5em 0' },
                                '& ul, & ol': { margin: '0.5em 0', paddingLeft: '1.5em' },
                                '& li': { margin: '0.25em 0' },
                                '& h1, & h2, & h3, & h4, & h5, & h6': { margin: '0.5em 0 0.25em' },
                                '& code': {
                                  backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)',
                                  padding: '0.1em 0.3em',
                                  borderRadius: '3px',
                                  fontSize: '0.9em',
                                },
                                '& pre': {
                                  backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)',
                                  padding: '0.5em',
                                  borderRadius: '4px',
                                  overflowX: 'auto',
                                  margin: '0.5em 0',
                                },
                                '& blockquote': {
                                  borderLeft: `3px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-4)'}`,
                                  margin: '0.5em 0',
                                  paddingLeft: '0.75em',
                                  color: isDark ? 'var(--mantine-color-dark-2)' : 'var(--mantine-color-gray-7)',
                                },
                                '& table': {
                                  borderCollapse: 'collapse',
                                  width: '100%',
                                  margin: '0.5em 0',
                                },
                                '& th, & td': {
                                  border: `1px solid ${isDark ? 'var(--mantine-color-dark-4)' : 'var(--mantine-color-gray-3)'}`,
                                  padding: '0.5em',
                                  textAlign: 'left',
                                },
                                '& th': {
                                  backgroundColor: isDark ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-1)',
                                  fontWeight: 600,
                                },
                              }}
                            >
                              <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={markdownComponents}
                              >
                                {message}
                              </ReactMarkdown>
                            </Box>
                          ) : (
                            <Text c="dimmed" size="sm" style={{ fontStyle: 'italic' }}>
                              Preview will appear here...
                            </Text>
                          )}
                        </Box>
                      </Box>
                    </Group>

                    <Group mt="xs" justify="flex-end">
                      <Button
                        onClick={handleClear}
                        variant="subtle"
                        size="xs"
                        color="red"
                        disabled={messages.length === 0}
                        title="Clear chat"
                      >
                        <TrashIcon width={14} style={{ marginRight: '4px' }} />
                        Clear
                      </Button>
                      <Button
                        onClick={handleSend}
                        disabled={!message.trim() || isLoading}
                        variant="filled"
                        size="sm"
                        loading={isLoading}
                      >
                        <PaperAirplaneIcon width={14} style={{ marginRight: '4px' }} />
                        Send
                      </Button>
                    </Group>
                  </Paper>
                </Box>
              )}
            </Paper>
          </Box>
        </Container>
        {/* CV Preview Sidebar */}
        {selectedPersonId && (
          <Box
            style={{
              position: 'fixed',
              top: 60,
              right: 0,
              width: 400,
              height: 'calc(100vh - 60px)',
              zIndex: 100,
              boxShadow: '-2px 0 10px rgba(0, 0, 0, 0.1)',
              background: 'white',
            }}
          >
            <CVPreviewSidebar
              personId={selectedPersonId}
              onClose={() => setSelectedPersonId(null)}
            />
          </Box>
        )}

        {/* Table Size Modal */}
        <Modal
          opened={showTableModal}
          onClose={() => setShowTableModal(false)}
          title="Insert Table"
          centered
          size="sm"
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Specify the dimensions for your table:
            </Text>
            <NumberInput
              label="Number of Rows"
              description="Data rows (excluding header)"
              placeholder="Enter number of rows"
              value={tableRows}
              onChange={(value) => setTableRows(typeof value === 'number' ? value : 3)}
              min={1}
              max={20}
              required
            />
            <NumberInput
              label="Number of Columns"
              description="Table columns"
              placeholder="Enter number of columns"
              value={tableColumns}
              onChange={(value) => setTableColumns(typeof value === 'number' ? value : 3)}
              min={1}
              max={10}
              required
            />
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={() => setShowTableModal(false)}
              >
                Cancel
              </Button>
              <Button
                variant="filled"
                onClick={handleTableInsert}
                disabled={tableRows < 1 || tableColumns < 1}
              >
                Insert Table
              </Button>
            </Group>
          </Stack>
        </Modal>
      </AppShell.Main>
    </AppShell>
  );
} 
